import { usePricingRights } from '@util/PricingRightsHook';
import React from 'react';
import Utilities from '@util/Utilities';
import Tag from '@app/component/tag/Tag';
import { PresentationChartLineIcon } from '@heroicons/react/24/outline';
import CalendarDay from '@app/state/calendar/CalendarDay';
import MarketStateSnapshotCalendarDayTableRow
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/MarketStateSnapshotCalendarDayTableRow';
import { PromotionTooltip }
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/DisplayMarketStateItemPrice';
import { ExclamationTriangleIcon, InformationCircleIcon, TagIcon } from '@heroicons/react/20/solid';
import { isCalendarSnapshotOutdated } from '@util/CalendarEmbed.utilities';
import MarketStateSnapshotDailyAlertNotificationsFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateSnapshotDailyAlertNotificationsFragment';
import {
	useMarketAreaStateForStatDate,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketAreaStateForMonth';
import {
	DisplayMarketAreaStateTag,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/component/DisplayMarketAreaStateTag';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketStateDailySnapshotWithComparisonOutput
	from '@app/model/market_state/output/MarketStateDailySnapshotWithComparisonOutput';
import { MarketStateStatDateCompetitorSnapshot } from '@app/model/market_state/MarketStateStatDateCompetitorSnapshot';
import MarketStateSnapshotDailyMarketInsightFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateSnapshotDailyMarketInsightFragment';
import MarketStateSnapshotCalendarDayPriceRecommendationTableRow
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/MarketStateSnapshotCalendarDayPriceRecommendationTableRow';
import MarketStateSnapshotDailyRecommendationLiteNotificationFragment
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateSnapshotDailyRecommendationLiteNotificationFragment';
import PriceSubmissionsForStatDateDrawer
	from '@app/site/secured/rateshopper/pricing-automation/price-submission-log/overlay/PriceSubmissionsForStatDateDrawer';
import useDrawer from '@app/component/overlays/drawer/DrawerRef';
import AvailabilityDailyStateProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';

export type MarketStateSnapshotCalendarDayTableCompetitorSnapshots = {
	current: MarketStateStatDateCompetitorSnapshot,
	previous: MarketStateStatDateCompetitorSnapshot | undefined
}[];

const MarketStateSnapshotCalendarDayTable: React.FunctionComponent<{
	calendarDay: CalendarDay,
	marketStateDailySnapshot: MarketStateDailySnapshotWithComparisonOutput,
	competitorSnapshots: MarketStateSnapshotCalendarDayTableCompetitorSnapshots,
	onOpenTimelineGraph: () => any
}> = ({
				calendarDay,
				marketStateDailySnapshot,
				competitorSnapshots,
				onOpenTimelineGraph,
			}) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const { canViewRecommendations, canChangePrices } = usePricingRights(companyHotel);

	const marketAreaStateComparisonOutput = useMarketAreaStateForStatDate(calendarDay?.display);
	const currentMarketAreaState = marketAreaStateComparisonOutput?.current;
	const recommendationLogsDrawer = useDrawer();

	const isOutdated = isCalendarSnapshotOutdated(calendarDay.display, marketStateDailySnapshot.current.latestUpdateDate);

	const recommendationIndex = marketStateDailySnapshot.findRecommendationIndex();
	const recommendation = !calendarDay.isPast ? marketStateDailySnapshot.priceRecommendationStateSnapshot?.recommendation : undefined;
	const isPricingSubmitted = marketStateDailySnapshot?.priceRecommendationStateSnapshot?.isSubmitted;
	const showPricingAction = canViewRecommendations || canChangePrices;
	const showRecommendation = !!recommendation && recommendation.isValid && !isPricingSubmitted
		&& canViewRecommendations && companyHotel.hasRecommendationNormalEngineRoompulseOrBraintree();

	return <div className="inner-wrapper min-w-0 p-2 text-left xl:min-w-[850px] sm:p-4">
		<h3 className="text-lg font-semibold leading-6 text-gray-900">
			{Utilities.formatDate(calendarDay.date, 'DD MMMM YYYY')}
		</h3>
		<div className="mt-6">
			<div className="pt-2 space-y-2">
				<div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
					<span className="text-sm font-medium text-gray-500">{companyHotel.comparisonLabel()} Compset Rate</span>
					<div className="flex flex-wrap gap-x-1">
						<span className="text-xs font-medium text-gray-400 italic">Updated:</span>
						{isOutdated && <ExclamationTriangleIcon className="inline-block h-4 w-4 text-yellow-400 mx-1" />}
						<span
							className="text-xs font-medium text-gray-500">{Utilities.formatDateString(marketStateDailySnapshot.current.latestUpdateDate!, 'DD MMMM HH:mm')}</span>
					</div>

				</div>
				<div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex items-end text-center">
						<span className="text-2xl font-medium tracking-tight text-gray-900 leading-5 pb">
								{!!marketStateDailySnapshot.current.comparisonPrice(companyHotel) ? Utilities.formatPrice(marketStateDailySnapshot.current.comparisonPrice(companyHotel)!) : companyHotel.usesWeightedAverage() ? 'N/A' : '--'}
						</span>
						<div className="relative flex items-center gap-x-1 ml-3">
							<span className="shrink-0 w-1.5 h-1.5 bg-gray-200 mr-1 rounded-full"></span>
							<span className="text-xs font-normal text-gray-400">Your difference</span>
							{marketStateDailySnapshot.current.evaluatePriceMedian(companyHotel)
								.map((result, idx) =>
									<Tag key={`index_${idx}`}
									     state={result.state}>{result.value}</Tag>)}
							{currentMarketAreaState && !companyHotel.hasMarketInsightModule() && <>
								<span className="shrink-0 w-1.5 h-1.5 bg-gray-200 mr-1 rounded-full"></span>
								<span className="text-xs font-normal text-gray-400">Market demand</span>
								<DisplayMarketAreaStateTag output={marketAreaStateComparisonOutput} />
							</>}
						</div>
						{/*<AvailabilityWeightComparisonWithCompetitors marketStateDailySnapshot={marketStateDailySnapshot} />*/}
					</div>
					<div>
						<a onClick={() => onOpenTimelineGraph()}
						   className="group flex items-center space-x-2 text-sm font-medium text-blue-600 hover:text-blue-900 cursor-pointer">
							<PresentationChartLineIcon className="w-4 h-4" />
							<span>Open Rate Timeline</span>
						</a>
					</div>

				</div>
			</div>
		</div>

		<MarketStateSnapshotDailyAlertNotificationsFragment calendarDay={calendarDay} />
		{!calendarDay.isPast
			&& (canViewRecommendations && companyHotel.hasRecommendationEngineRoompulseLite())
			&& !!marketStateDailySnapshot.current.findDailyMainHotelSnapshot()
			&& <MarketStateSnapshotDailyRecommendationLiteNotificationFragment
				calendarDay={calendarDay}
				marketStatDateMainHotelSnapshot={marketStateDailySnapshot.current.findDailyMainHotelSnapshot()!} />}

		{currentMarketAreaState && companyHotel.hasMarketInsightModule() &&
			<MarketStateSnapshotDailyMarketInsightFragment calendarDay={calendarDay}
			                                               marketAreaStateComparison={marketAreaStateComparisonOutput} />}
		<div className="mt-6">
			{(canViewRecommendations && companyHotel.hasRecommendationEngineRoompulse()) &&
				<div className="text-right mb-1">
					<a className="text-sm text-blue-600 font-semibold hover:text-blue-900 cursor-pointer"
					   onClick={() => recommendationLogsDrawer.open()}>• Recommendation Log</a>
					<PriceSubmissionsForStatDateDrawer statDate={calendarDay.display} ref={recommendationLogsDrawer.ref} />
				</div>}
			<div className="overflow-auto shadow ring-1 ring-black ring-opacity-5 rounded-md">
				<div className="inline-block min-w-full align-middle max-h-[500px]">
					<table className="min-w-full divide-y divide-gray-300">
						<thead className="bg-gray-50 sticky-table-header-only z-0">
						<tr>
							<th scope="col"
							    className="py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
								Hotel
							</th>
							<th scope="col"
							    className="py-3 px-3 text-left text-sm font-semibold text-gray-900">
								Price
							</th>
							<th scope="col"
							    className="w-[52px] py-3 px-0 text-center text-sm font-semibold text-gray-900">
								<span className="inline-flex items-center justify-center gap-x-1">
									Flex
									<InformationCircleIcon className="h-3.5 w-3.5 text-gray-400"
									                       data-tooltip-id="rates_flex_legend_tooltip"
									                       data-tooltip-content="F-7 counts days before arrival; a larger number means an earlier cancellation deadline. FP marks a partly refundable rate. L4 counts nights of stay." />
								</span>
							</th>
							<th scope="col"
							    className="w-px py-3 px-1 text-center text-sm font-semibold text-gray-900">
							</th>
							<th scope="col"
							    className="w-px py-3 px-1 text-center text-sm font-semibold text-gray-900"
							    title="Booking.com promotions & partner tier">
								<span className="sr-only">Booking.com promotions and partner tier</span>
								<TagIcon aria-hidden="true" className="mx-auto h-4 w-4 text-gray-400" />
							</th>
							<th scope="col"
							    className="py-3 px-3 text-left text-sm font-semibold text-gray-900">
								Availability
							</th>
							<th scope="col"
							    className="py-3 px-3 text-left text-sm font-semibold text-gray-900">
								Type
							</th>
							<th scope="col"
							    className="py-3 px-3 text-left text-sm font-semibold text-gray-900" />
							{showPricingAction && <th scope="col"
							                                   className="py-3 px-3 text-left text-sm font-semibold text-gray-900" />}
						</tr>
					</thead>
					<tbody className="divide-y divide-gray-200 bg-white">
						<AvailabilityDailyStateProvider companyHotel={companyHotel} statDate={marketStateDailySnapshot.current.statDate}>
							{competitorSnapshots.map((item, idx) => {
									return <React.Fragment key={`market-state-hotel-${idx}`}>
										{showRecommendation && recommendationIndex === idx &&
											<MarketStateSnapshotCalendarDayPriceRecommendationTableRow marketStateForStatDate={marketStateDailySnapshot} />}

										<MarketStateSnapshotCalendarDayTableRow currentMarketStateForDay={marketStateDailySnapshot.current}
										                                        priceRecommendationState={marketStateDailySnapshot.priceRecommendationStateSnapshot}
																														calendarDay={calendarDay}
										                                        current={item.current}
										                                        previous={item.previous} />
									</React.Fragment>;
								},
							)}
							{showRecommendation && recommendationIndex === competitorSnapshots.length &&
								<MarketStateSnapshotCalendarDayPriceRecommendationTableRow marketStateForStatDate={marketStateDailySnapshot} />}
						</AvailabilityDailyStateProvider>
						</tbody>
					</table>
				</div>
			</div>
			<PromotionTooltip id="calendar_day_promotion_tooltip" />
		</div>
	</div>;
};

export default MarketStateSnapshotCalendarDayTable;
